import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Prompt} from 'react-router';
import {push} from 'connected-react-router/immutable';
import {Trans} from '@lingui/react';
import {i18n} from '../../../services/i18n';

import {QUESTIONNAIRE360} from '../../../services/routes';
import {getUserCampaignId, getUserCampaign360Range} from '../../../reducers/userCampaign';
import {
  loadQuestions,
  loadEvaluation,
  saveOutcome,
  loadStore,
  saveLinkedAnswer,
} from '../../../actions/evaluationQuestions';
import {getTestExitMessage, getEvaluationCompleteMessage} from '../../../services/alerts';

import Questionnaire from '../../../components/Skillup360/Questionnaire360';
import TopBar from '../../../components/Skillup360/TopBar';

import s from './Questionnaire360Container.css';

export const loadingStates = ['IDLE', 'PENDING'];

export default function Questionnaire360Container(props) {
  const {location} = props;
  const evaluee = location?.state?.evaluee;
  const isSelfEval = location?.state?.isSelfEval;

  const dispatch = useDispatch();

  const closedQuestions = useSelector(state =>
    state.getIn(['evaluationQuestions', 'questions', 'closedQuestions'])
  );
  const openQuestions = useSelector(state =>
    state.getIn(['evaluationQuestions', 'questions', 'openQuestions'])
  );
  const linkedQuestions = useSelector(state =>
    state
      .getIn(['evaluationQuestions', 'questions', 'linkedQuestions'])
      ?.sort((a, b) => a.get('id') - b.get('id'))
  );
  const store = useSelector(state => state.getIn(['evaluationQuestions', 'store']));
  const evaluation = useSelector(state => state.getIn(['evaluationQuestions', 'evaluation']));

  const questionsLoadingState = useSelector(state =>
    state.getIn(['evaluationQuestions', 'questionLoadingState'])
  );
  const storeLoadingState = useSelector(state =>
    state.getIn(['evaluationQuestions', 'storeLoadingState'])
  );
  const evaluationLoadingState = useSelector(state =>
    state.getIn(['evaluationQuestions', 'evaluationLoadingState'])
  );

  const jsonOptions = useSelector(state => state.getIn(['tests', 'current', 'jsonData']));
  const userId = useSelector(state => state.getIn(['user', 'data', 'id']));
  const toEvaluateUsers = useSelector(state => state.getIn(['questionnaire360', 'data']));
  const campaignId = useSelector(getUserCampaignId);
  const range = useSelector(getUserCampaign360Range);
  const optionRange = parseInt(range);

  const [itemsPerPage] = useState(10);
  const [currPage, setCurrPage] = useState(1);

  const [promptEnabled, setPromptEnabled] = useState(false);

  useEffect(() => {
    if (!campaignId || !evaluee) {
      dispatch(push(QUESTIONNAIRE360));
      return;
    }

    dispatch(loadQuestions({campaignId, evaluee}));
    dispatch(loadEvaluation({campaignId, evaluee}));
    dispatch(loadStore({campaignId, evaluee}));

    setPromptEnabled(true);

    return () => {
      setPromptEnabled(false);
    };
  }, []);

  useEffect(() => {
    if (!closedQuestions && !openQuestions && !evaluation) return;

    if (!jsonOptions) {
      dispatch(push(QUESTIONNAIRE360));
      return;
    }
  }, [evaluation, closedQuestions, openQuestions, jsonOptions]);
  function getQuestionData() {
    if (!closedQuestions || !openQuestions)
      return {
        isLocked: true,
        currentQuestion: null,
        questionCount: 0,
        questionType: 'closed',
        options: null,
        currentColumnIndex: 0,
      };

    let isLocked = true;
    let options;
    let questionCount;
    let question;
    let question_type = 'closed';
    let currentColumnIndex = 0;

    const total_closed_questions_pages = Math.ceil(closedQuestions?.size / itemsPerPage);
    const openQuestionsNum = openQuestions.size;
    const linkedQuestionsNum = linkedQuestions.size;
    const closedQuestionsNum = closedQuestions.size;
    
    // Calcola il numero totale di colonne per tutte le linked questions
    const lq_len = linkedQuestions?.reduce((total, question) => {
      const columns = question.getIn(['columns']);
      return total + (columns?.size || 0);
    }, 0) || 0;

    // Determina il tipo di domanda
    if (currPage > total_closed_questions_pages && currPage <= total_closed_questions_pages + lq_len) {
      question_type = 'linked';
    } else if (currPage > total_closed_questions_pages + lq_len) {
      question_type = 'open';
    }

    let from = 0;
    let to = question_type === 'closed' ? itemsPerPage : 1;

    if (question_type === 'closed') {
      if (currPage > 1) {
        from = (currPage - 1) * itemsPerPage;
        to = from + itemsPerPage;
      }

      question = closedQuestions.slice(from, to);
      questionCount = (currPage - 1) * itemsPerPage + question.size;
    }

    if (question_type === 'linked') {
      // Calcola quale linked question e quale colonna
      const linkedPageOffset = currPage - total_closed_questions_pages;
      let accumulatedColumns = 0;
      
      for (let i = 0; i < linkedQuestions.size; i++) {
        const lq = linkedQuestions.get(i);
        const columns = lq.getIn(['columns']);
        const columnsSize = columns ? columns.size : 1;
        
        if (linkedPageOffset <= accumulatedColumns + columnsSize) {
          // Trovata la domanda corretta
          question = lq;
          currentColumnIndex = linkedPageOffset - accumulatedColumns - 1;
          questionCount = closedQuestionsNum + linkedPageOffset;
          break;
        }
        
        accumulatedColumns += columnsSize;
      }
    }

    if (question_type === 'open') {
      const openPageOffset = currPage - total_closed_questions_pages - lq_len;
      
      question = openQuestions.slice(openPageOffset - 1, openPageOffset);
      questionCount = closedQuestionsNum + lq_len + openPageOffset;
    }    if (evaluation) {
      if (question_type !== 'linked') {
        const hasResponded = question.every(q => {
          const item = evaluation.find(e => e.get('questionId') === q.get('id'));

          if (!item) {
            return false;
          }

          if (question_type === 'closed' && item.get('value')) {
            return true;
          } else if (question_type === 'open' && item.get('textValue')) {
            return true;
          } else {
            return false;
          }
        });
        isLocked = !hasResponded;

        const parsed_options = JSON.parse(jsonOptions);
        //for compatibility sake this may be an array
        //from now on we'll get an object with an options key
        let optionsCompatibility = Array.isArray(parsed_options)
          ? parsed_options
          : parsed_options.options;

        if (optionRange === 4) {
          optionsCompatibility = optionsCompatibility.filter(o => o.value !== 3);
        }

        options = optionsCompatibility;
      } else {
        // Per linked questions, verifica se la colonna corrente è completa
        const minAnswers = question.get('min_answers');
        const columns = question.get('columns');
        const currentColumn = columns.get(currentColumnIndex);
        
        if (currentColumn) {
          const currentColumnId = currentColumn.get('order');
          const storeAnswers = store.data?.[userId];
          
          let answersGivenForCurrentColumn = 0;
          
          if (storeAnswers) {
            const storeAnswersForThisQuestion = storeAnswers.filter(
              item => item.questionId === question.get('id')
            );
            
            for (let answer of storeAnswersForThisQuestion) {
              if (answer.value === currentColumnId) {
                answersGivenForCurrentColumn++;
              }
            }
          }
          
          // La colonna è completa se ha almeno minAnswers risposte
          isLocked = answersGivenForCurrentColumn < minAnswers;
        } else {
          isLocked = true;
        }
      }
    }return {
      currentQuestion: question,
      questionType: question_type,
      questionCount,
      isLocked,
      options,
      currentColumnIndex,
    };
  }

  function handlePreviousClick() {
    if (currPage <= 1) return;

    setCurrPage(page => page - 1);
  }

  function handleNextClick() {
    if (currPage >= totalPage) {
      handleQuestionnaireCompleted();
      return;
    }

    setCurrPage(page => page + 1);
  }
  function handleSaveAnswers(data) {
    let data_to_save = [];

    //data for linked questions and closed questions differ
    //a lot
    if (questionType === 'linked') {
      const dataMap = new Map();
      //create map with new question to save
      //so I can append to array only if curr question
      //doesn't exist in store already
      data.get('answers').forEach(answer => {
        dataMap.set(`${data.get('id')}-${answer.get('id')}`, answer.value);
        data_to_save.push({
          questionId: data.get('id'),
          answerId: answer.get('id'),
          value: answer.get('value') || null,
        });
      });

      const storeAnswers = store.data?.[userId];
      if (storeAnswers) {
        for (let answer of storeAnswers) {
          if (!dataMap.has(`${answer.questionId}-${answer.answerId}`)) {
            data_to_save.push({
              questionId: answer.questionId,
              answerId: answer.answerId,
              value: answer.value,
            });
          }
        }
      }

      //if it's linked type
      //save to evaluee store instead
      const stringifiedData = JSON.stringify({...store.data, [userId]: data_to_save});
      const questionId = currentQuestion.get('id');
      
      // Verifica se tutte le colonne sono completate prima di inviare al backend
      const minAnswers = currentQuestion.get('min_answers');
      const columns = currentQuestion.get('columns');
      
      const answersGivenAcc = new Map();
      for (let answer of data.get('answers')) {
        const value = answer.get('value');
        if (value !== undefined && value !== null) {
          const valueUntilNow = answersGivenAcc.get(value) || 0;
          answersGivenAcc.set(value, valueUntilNow + 1);
        }
      }
      
      let allColumnsComplete = true;
      for (const column of columns) {
        const columnId = column.get('order');
        const answersGiven = answersGivenAcc.get(columnId) || 0;
        if (answersGiven < minAnswers) {
          allColumnsComplete = false;
          break;
        }
      }
      
      // Salva nello store locale sempre
      dispatch({
        type: 'STORE_LOADING_SUCCESS',
        data: {data: JSON.parse(stringifiedData), version: store.version},
      });
      
      // Invia al backend solo se tutte le colonne sono completate
      if (allColumnsComplete) {
        dispatch(
          saveLinkedAnswer({
            campaignId,
            evaluee,
            data: stringifiedData,
            version: store.version,
            userId,
            questionId,
            onSuccess: handleNextClick,
          })
        );
      } else {
        // Se non tutte le colonne sono complete, vai solo alla pagina successiva
        handleNextClick();
      }
      return;
    }

    data_to_save = data.map(d => ({
      questionId: d.id,
      value: (questionType === 'closed' ? d.value : d.textValue) || null,
    }));

    dispatch(saveOutcome({campaignId, evaluee, data: data_to_save, onSuccess: handleNextClick}));
  }

  function handleQuestionnaireCompleted() {
    const notYetEvaluatedUsers = toEvaluateUsers.filter(
      user => user.get('user_is_evaluated') === 'n'
    );

    if (notYetEvaluatedUsers.size === 1) {
      //TODO: add call to endpoint when it's ready
      //to set status complete for 360 test
    }

    dispatch(push(QUESTIONNAIRE360));
  }

  function getExitMessage() {
    const isComplete = currPage >= totalPage;
    return isComplete
      ? getEvaluationCompleteMessage(
          i18n._('questionnaire360container.skillup360_dashboard_redirect_message', null, {
            defaults: 'Sarai reindirizzato al dashboard di Skillup360',
          })
        )
      : getTestExitMessage();
  }
  const {questionCount, questionType, currentQuestion, isLocked, options, currentColumnIndex} = getQuestionData();
  //trova la len di tutte le colonne di linked qustions
  var lq_len =
    linkedQuestions?.reduce((total, question) => {
      const columns = question.getIn(['columns']);
      return total + (columns?.size || 0);
    }, 0) || 0;
  const totalQuestions = closedQuestions?.size + openQuestions?.size + lq_len;
  const totalPage = closedQuestions
    ? Math.ceil(closedQuestions.size / itemsPerPage) + (openQuestions?.size || 0) + (lq_len || 0)
    : null;
  const isLoading =
    loadingStates.includes(questionsLoadingState) ||
    loadingStates.includes(storeLoadingState) ||
    loadingStates.includes(evaluationLoadingState);

  // Find the user being evaluated
  const evaluatedUser = toEvaluateUsers?.find(user => user.get('id') === evaluee);
  const evaluatedUserName = evaluatedUser
    ? `${evaluatedUser.get('firstName')} ${evaluatedUser.get('lastName')}`
    : '';

  return (
    <div className={s.Questionnaire360Container}>
      <Prompt when={promptEnabled} message={getExitMessage} />

      <TopBar
        currentPage={currPage}
        totalPage={totalPage}
        pageLocked={isLocked}
        onPrevious={handlePreviousClick}
        onNext={handleNextClick}
        evaluatedUserName={evaluatedUserName}
      />

      {!isLoading && totalQuestions < 1 ? (
        <h3 className={s.NoQuestions}>
          <Trans id="questionnaire360container.no_questions">Nessun domande disponibile</Trans>
        </h3>
      ) : null}      {!isLoading && totalQuestions > 0 ? (
        <Questionnaire
          key={currPage}
          currPage={currPage}
          total={totalQuestions}
          count={questionCount}
          isSelfEval={isSelfEval}
          options={options}
          store={store?.data}
          evaluation={evaluation}
          type={questionType}
          currentQuestion={currentQuestion}
          onSave={handleSaveAnswers}
          totalPage={totalPage}
          currentColumnIndex={currentColumnIndex}
          evaluatedUserName={evaluatedUserName}
        />
      ) : null}
    </div>
  );
}
